import get from 'lodash/get';
import set from 'lodash/set';
import snakeCase from 'lodash/snakeCase';
import { all, call, put, select, take } from 'typed-redux-saga/macro';

import { getRoute } from 'Config';
import {
  I13N_ACTION_COMPLETE,
  I13N_ACTION_DELETE,
  I13N_ACTION_PROGRESS,
  I13N_ACTION_START,
  I13N_CATEGORY_ONBOARDING,
  I13N_TYPE_FEATURE_COUNT,
} from 'Lib/constants/i13nSpec';
import { LOGIN_TYPE_FACEBOOK } from 'Lib/constants/loginTypes';
import { UNDERAGE_USER_ERROR } from 'Lib/errors';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import { GetActionByType } from 'Lib/reduxType';
import { processResponse } from 'Lib/reduxUtils';
import { getWardenRedirect, withErrorHandling } from 'Lib/sagaHelpers';
import { decamelizeKeys } from 'Lib/utils/createKeyProcessor';
import { pick } from 'Lib/utils/lodashUtils';
import { getServiceFn } from 'Services';
import { ONBOARDING_SERVICE } from 'Services/constants';

import { deviceFlaggedForDobViolation } from 'Store/app/actions';
import { getAuthAttributionData } from 'Store/app/selectors';
import { AuthAttributionData } from 'Store/app/types';
import { setBanPolicy } from 'Store/appeals/actions';
import { getLayout } from 'Store/appSettings/selectors';
import { LAYOUT_DESKTOP } from 'Store/commonActions';
import { getQueryParams } from 'Store/context/selectors';
import { getIncentiveCampaignName } from 'Store/incentives/selectors';
import { uploadMediaSaga } from 'Store/media/logic';
import { MediaInfoType } from 'Store/media/types';
import { loginFailure } from 'Store/mfa/actions';
import { getAuthFunnelSessionId, getLoginType, getRefreshToken } from 'Store/mfa/selectors';
import { openModal } from 'Store/modals/actions';
import { MODAL_CONFIRMATION, MODAL_HOUSE_RULES } from 'Store/modals/constants';
import { showNotification } from 'Store/notifications/actions';
import { navigate } from 'Store/routing/actions';
import { setIsUnderageOnboarding, setUnderageToken } from 'Store/user/actions';
import { getIncentivePromoCode } from 'Store/user/selectors';
import { biometricsBlockingConsentInit } from 'Store/userBiometrics/actions';
import {
  BIOMETRICS_IS_COMPLETED_SUCCESS,
  BIOMETRICS_ONBOARDING_FLOW,
} from 'Store/userBiometrics/constants';
import { getIsBiometricsCompletedSuccess } from 'Store/userBiometrics/selectors';

import { trackEvent } from 'Store/sparks/actions';
import { ONBOARDING_EVENT } from 'Store/sparks/sparks.events';

import { NOTIFICATION_THEME_DARK } from 'Components/InAppNotification/constants';

import {
  clearQueuedPhotos,
  completeOnboarding,
  completeOnboardingError,
  completeOnboardingSuccess,
  deleteOnboardingSuccess,
  fetchOnboardingFields,
  fetchOnboardingFieldsError,
  fetchOnboardingFieldsRequest,
  fetchOnboardingFieldsSuccess,
  onboardingSparks,
  setOnboardingStep,
  setOnboardingSteps,
  updateOnboardingFieldsError,
  updateOnboardingFieldsRequest,
  updateOnboardingFieldsSuccess,
} from './actions';
import {
  COMPLETE_ONBOARDING_SUCCESS,
  DELETE_ONBOARDING,
  FETCH_ONBOARDING_FIELDS_ERROR,
  FETCH_ONBOARDING_FIELDS_SUCCESS,
  ONBOARDING_SKIP_ACTION,
  ONBOARDING_SPARKS,
  ONBOARDING_SUBMIT_ACTION,
  UPDATE_ONBOARDING_FIELDS,
} from './constants';
import { onboardingFieldsResponseSchema } from './schemas';
import {
  getIsReactivated,
  getOnboardingEditable,
  getOnboardingInitialValue,
  getOnboardingNextStep,
  getOnboardingProgress,
  getOnboardingRequired,
  getOnboardingRequiredFields,
  getQueuedPhotos,
} from './selectors';
import type { AuthOnboardingValues } from './types';
import { determineOnboardingSteps, errorCodeMessages, getSparksEventData } from './utils';

const { executeEvent } = getI13nUtils();

// Loosely keep track of the attempt number for each field. Onboarding is not built in such a way
// that this is easy to do accurately and persistently, so this is just a best effort.
const onboardingFieldAttempts = {};

function getFieldAttempts(field: string, increment?: boolean) {
  if (onboardingFieldAttempts[field] == null) {
    onboardingFieldAttempts[field] = 0;
  }
  if (increment) {
    onboardingFieldAttempts[field] += 1;
  }
  return onboardingFieldAttempts[field];
}

function resetFieldAttempts(field?: string) {
  if (field) {
    onboardingFieldAttempts[field] = 0;
  } else {
    Object.keys(getFieldAttempts).forEach((f) => {
      onboardingFieldAttempts[f] = 0;
    });
  }
}

/**
 * Gets the onboarding fields, which determines how far the user is into onboarding and what
 * information we already have to create their account
 */
export const fetchOnboardingFieldsSaga = withErrorHandling(
  function* _fetchOnboardingFieldsSaga() {
    yield* put(fetchOnboardingFieldsRequest());

    const funnelSessionId = yield* select(getAuthFunnelSessionId);
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    const response = yield* call(
      yield* call(getServiceFn, ONBOARDING_SERVICE, 'getFields' as const),
      funnelSessionId
    );
    const payload = yield processResponse(response, onboardingFieldsResponseSchema);
    if (get(payload, ['result', 'isUnderage'], false)) {
      const birthDate = get(payload, ['result', 'values', 'birthDate']);
      yield* put(deviceFlaggedForDobViolation(birthDate));
    }
    const results = get(payload, 'result', {});
    yield* put(fetchOnboardingFieldsSuccess(payload));

    const isReactivatedAccount = yield* select(getIsReactivated);
    const { steps, firstStepToShow } = determineOnboardingSteps(results, isReactivatedAccount);
    yield* put(setOnboardingSteps(steps, firstStepToShow));
  },
  {
    name: 'fetchOnboardingFieldsSaga',
    *errorHandler(err) {
      yield* put(fetchOnboardingFieldsError(err));
      if (err.name === UNDERAGE_USER_ERROR) {
        yield* put(deviceFlaggedForDobViolation(null, err.ttl));
      }
    },
  }
);

function* retrieveOnboardingUserData() {
  yield* put(fetchOnboardingFields());
  yield* take([FETCH_ONBOARDING_FIELDS_SUCCESS, FETCH_ONBOARDING_FIELDS_ERROR]);
}

export const guestOnboardingSaga = withErrorHandling(
  function* guestOnboarding() {
    yield* call(retrieveOnboardingUserData);
  },
  {
    name: 'guestOnboardingSaga',
  }
);

export const onboardingRootSaga = withErrorHandling(
  function* onboardingRoot() {
    executeEvent(I13N_TYPE_FEATURE_COUNT, {
      category: I13N_CATEGORY_ONBOARDING,
      action: I13N_ACTION_START,
      label: 'Onboarding',
      message: 'Start',
    });

    yield* call(retrieveOnboardingUserData);
    // @TODO: Handle when this errors?

    const requiredFields = yield* select(getOnboardingRequiredFields);
    const isBiometricRequired = requiredFields.biometricLiveness;
    const isBiometricsCompletedSuccess = yield* select(getIsBiometricsCompletedSuccess);

    if (isBiometricRequired && !isBiometricsCompletedSuccess) {
      yield* put(biometricsBlockingConsentInit(BIOMETRICS_ONBOARDING_FLOW));
      yield* take(BIOMETRICS_IS_COMPLETED_SUCCESS);
    }

    yield* put(navigate(getRoute('onboarding')));

    // We should listen here to see if onboarding has been cancelled
    // at that point, we should call API.onboardingExit() to clear the onboarding token
    // yield takeEvery('ONBOARDING_CANCELLED', _onboardingCancelledSaga);

    yield* put(
      openModal(MODAL_HOUSE_RULES, {
        props: {
          forceOpen: true,
        },
      })
    );

    yield* take(COMPLETE_ONBOARDING_SUCCESS);

    executeEvent(I13N_TYPE_FEATURE_COUNT, {
      category: I13N_CATEGORY_ONBOARDING,
      action: I13N_ACTION_COMPLETE,
      label: 'Onboarding',
      message: 'Complete',
    });
  },
  {
    name: 'onboardingRootSaga',
  }
);

export const deleteOnboardingSaga = withErrorHandling(
  function* _deleteOnboardingSaga(action: GetActionByType<typeof DELETE_ONBOARDING>) {
    const funnelSessionId = yield* select(getAuthFunnelSessionId);
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    yield* call(
      yield* call(getServiceFn, ONBOARDING_SERVICE, 'deleteOnboarding' as const),
      funnelSessionId
    );
    executeEvent(I13N_TYPE_FEATURE_COUNT, {
      category: I13N_CATEGORY_ONBOARDING,
      action: I13N_ACTION_DELETE,
      label: 'Onboarding',
      message: 'Delete onboarding',
    });

    yield* put(deleteOnboardingSuccess());
    if (get(action.payload, 'redirect')) {
      yield* put(navigate(getRoute('login', {}, { withPrefix: false })));
    }
  },
  {
    name: 'deleteOnboardingSaga',
  }
);

export const completeOnboardingSaga = withErrorHandling(
  function* _completeOnboardingSaga() {
    const promoCode = yield* select(getIncentivePromoCode);
    const campaignName = yield* select(getIncentiveCampaignName);
    const authAttributionDataFromStore = yield* select(getAuthAttributionData);
    const queryParams = yield* select(getQueryParams);

    let authAttributionData: AuthAttributionData = authAttributionDataFromStore;
    if (queryParams.attributionId && queryParams.attributionSource) {
      authAttributionData = {
        attributionId: queryParams.attributionId,
        attributionSource: queryParams.attributionSource,
      };
    }

    const incentivesData = {
      promoCode,
      campaignName,
    };
    const refreshToken = yield* select(getRefreshToken);
    const funnelSessionId = yield* select(getAuthFunnelSessionId);
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    yield* call(yield* call(getServiceFn, ONBOARDING_SERVICE, 'completeOnboarding' as const), {
      incentivesData,
      refreshToken,
      authAttributionData,
      funnelSessionId,
    });
    yield* put(completeOnboardingSuccess());
    yield* call(resetFieldAttempts);
  },
  {
    name: 'completeOnboardingSaga',
    *errorHandler(err) {
      yield* put(completeOnboardingError());
      yield* put(loginFailure(err));

      const { name, policy } = err || {};

      if (policy) yield* put(setBanPolicy(policy));

      const wardenRedirect = getWardenRedirect(name, policy);

      if (name === UNDERAGE_USER_ERROR) {
        yield* put(deviceFlaggedForDobViolation(null, err.ttl));
      } else if (wardenRedirect) {
        yield* put(navigate(wardenRedirect));
      } else {
        yield* put(
          openModal(MODAL_CONFIRMATION, {
            props: {
              title: 'errorTitle',
              subtitles: 'ErrorPage.somethingWentWrong',
              acceptButton: 'okay',
              hideCancelButton: true,
            },
          })
        );
      }
    },
  }
);

export const updateOnboardingFieldsSaga = withErrorHandling(
  function* _updateOnboardingFieldsSaga(action: GetActionByType<typeof UPDATE_ONBOARDING_FIELDS>) {
    let isSubmission = get(action.payload, 'isSubmission');
    const values = get(action.payload, 'values');
    const sparksEventValue = get(action.payload, 'sparksEventValue', values);
    // Package fields to send to the API
    const fields = Object.keys(values)
      .filter((key) => key !== 'photo' && key !== 'consents' && values[key] !== null)
      .map((key) =>
        values[key] !== ''
          ? { name: snakeCase(key), data: decamelizeKeys(values[key]) }
          : { name: snakeCase(key), meta: { skipped: true } }
      );
    const funnelSessionId = yield* select(getAuthFunnelSessionId);

    // No fields? Maybe we were just updating the photo
    if (fields.length) {
      try {
        yield* put(updateOnboardingFieldsRequest());
        // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
        const response = yield* call(
          yield* call(getServiceFn, ONBOARDING_SERVICE, 'updateFields' as const),
          fields,
          funnelSessionId
        );
        const payload = yield processResponse(response, onboardingFieldsResponseSchema);

        if (get(payload, ['result', 'isUnderage'], false)) {
          // If age verification fails, we cannot continue
          isSubmission = false;
          const birthDate = get(payload, ['result', 'values', 'birthDate']);
          yield* put(deviceFlaggedForDobViolation(birthDate));
        }

        yield* put(updateOnboardingFieldsSuccess(payload));
        yield* put(onboardingSparks(sparksEventValue, 'submit', true));
      } catch (e: any) {
        // If it fails for any reason, we will not go to the next step, don't continue
        isSubmission = false;

        const { code } = e?.error || {};

        if (code) {
          executeEvent(I13N_TYPE_FEATURE_COUNT, {
            category: I13N_CATEGORY_ONBOARDING,
            action: I13N_ACTION_PROGRESS,
            label: 'Onboarding',
            message: `Update fields error - ${code}`,
          });
        }

        if (e.name === UNDERAGE_USER_ERROR) {
          const { ttl, underageToken, isOnboarding, isAppealable } = e;
          yield* put(setUnderageToken(underageToken));
          yield* put(setIsUnderageOnboarding(isOnboarding));
          yield* put(deviceFlaggedForDobViolation(null, ttl, isAppealable));
        }

        const errorCodeMessage = errorCodeMessages[code];
        if (errorCodeMessage) {
          const { field, id } = errorCodeMessage;
          yield* put(updateOnboardingFieldsError(field, id));
        } else {
          yield* all(
            Object.keys(values)
              .filter((key) => key !== 'photo' && values[key] !== null)
              .map((field) => put(updateOnboardingFieldsError(field, 'error')))
          );
        }
        yield* put(onboardingSparks(sparksEventValue || values, 'submit', false));
      }
    }

    if (values.photo instanceof Blob) {
      try {
        yield* put(updateOnboardingFieldsRequest());

        const gaPayload = {
          category: I13N_CATEGORY_ONBOARDING,
        };

        executeEvent(I13N_TYPE_FEATURE_COUNT, {
          ...gaPayload,
          action: I13N_ACTION_START,
          label: 'Onboarding',
          message: 'Upload photo start',
        });

        // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
        const response = yield* call(
          yield* call(getServiceFn, ONBOARDING_SERVICE, 'updatePhoto' as const),
          values.photo,
          funnelSessionId
        );
        const payload = yield processResponse(response, onboardingFieldsResponseSchema);

        executeEvent(I13N_TYPE_FEATURE_COUNT, {
          ...gaPayload,
          action: I13N_ACTION_COMPLETE,
          label: 'Onboarding',
          message: 'Upload photo complete',
        });

        // Only update the values we're expecting to modify so that we're not overwriting fields
        // from the previous request
        const filteredValues = pick(get(payload, ['result', 'values'], {}), ['photos']);
        set(payload, ['result', 'values'], filteredValues);

        yield* put(updateOnboardingFieldsSuccess(payload));
        yield* put(onboardingSparks({ photos: true }, 'submit', true));
      } catch (e: any) {
        // If it fails for any reason, we will not go to the next step, don't continue
        isSubmission = false;

        const { code } = e?.error || {};

        executeEvent(I13N_TYPE_FEATURE_COUNT, {
          category: I13N_CATEGORY_ONBOARDING,
          action: I13N_ACTION_PROGRESS,
          label: 'Onboarding',
          message: `Update fields error - ${code}`,
        });

        if (e.name === UNDERAGE_USER_ERROR) {
          yield* put(deviceFlaggedForDobViolation(null, e.ttl));
        }

        const errorCodeMessage = errorCodeMessages[code];
        if (errorCodeMessage) {
          const { field, id } = errorCodeMessage;
          yield* put(updateOnboardingFieldsError(field, id));
        } else {
          yield* put(updateOnboardingFieldsError('photos', 'error'));
        }
        yield* put(onboardingSparks({ photos: true }, 'submit', false));
      }
    }

    if (values.consents) {
      yield* put(updateOnboardingFieldsRequest());
      // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
      const response = yield* call(
        yield* call(getServiceFn, ONBOARDING_SERVICE, 'updateFields' as const),
        [
          {
            name: 'consents',
            data: values.consents,
          },
        ],
        funnelSessionId
      );

      const payload = yield processResponse(response, onboardingFieldsResponseSchema);
      yield* put(updateOnboardingFieldsSuccess({ payload }));
      yield* put(onboardingSparks(values, 'submit', true));
    }

    const progress = yield* select(getOnboardingProgress);
    if (progress) {
      executeEvent(I13N_TYPE_FEATURE_COUNT, {
        category: I13N_CATEGORY_ONBOARDING,
        action: I13N_ACTION_PROGRESS,
        label: 'Onboarding',
        message: progress,
      });
    }

    // Advance to the next step
    if (isSubmission) {
      const nextStep = yield* select(getOnboardingNextStep);
      const layout = yield* select(getLayout);
      if (nextStep && layout !== LAYOUT_DESKTOP) {
        yield* put(setOnboardingStep(nextStep));
      } else {
        yield* put(completeOnboarding());
      }
    }
  },
  {
    name: 'updateOnboardingFieldsSaga',
    *errorHandler(err) {
      // In cases where a user has previously tried an underage birthdate
      if (err.name === UNDERAGE_USER_ERROR) {
        yield* put(setUnderageToken(err.underageToken));
        yield* put(setIsUnderageOnboarding(err.isOnboarding));
        yield* put(deviceFlaggedForDobViolation(null, err.ttl, err.isAppealable));
      }
    },
  }
);

export const onboardingSparksSaga = withErrorHandling(
  function* _onboardingSparksSaga(action: GetActionByType<typeof ONBOARDING_SPARKS>) {
    const onboardingAction = get(action.payload, 'action', 'view');
    const shouldIncrement = onboardingAction === 'submit' || onboardingAction === 'skip';
    const fields = get(action.payload, 'fields');
    const success = get(action.payload, 'success', false);
    const loginType = yield* select(getLoginType);
    const eventVersion = loginType !== LOGIN_TYPE_FACEBOOK ? 1 : 2;

    const fieldNames = Object.keys(fields);
    const initialValues = yield* all(
      fieldNames.reduce((acc, name) => {
        // TODO: REMOVE-TYPE-ASSERTION - name is not guaranteed to be a valid key of AuthOnboardingValues.
        acc[name] = select(getOnboardingInitialValue, name as keyof AuthOnboardingValues);
        return acc;
      }, {})
    );
    const requiredFields = yield* all(
      fieldNames.reduce((acc, name) => {
        acc[name] = select(getOnboardingRequired, name);
        return acc;
      }, {})
    );
    const editableFields = yield* all(
      fieldNames.reduce((acc, name) => {
        acc[name] = select(getOnboardingEditable, name);
        return acc;
      }, {})
    );

    yield* all(
      fieldNames
        .map((name) => {
          const attempt = getFieldAttempts(name, shouldIncrement);
          const value = fields[name];
          const initialValue = initialValues[name];
          const eventAction =
            onboardingAction !== ONBOARDING_SUBMIT_ACTION || initialValue !== value
              ? onboardingAction
              : ONBOARDING_SKIP_ACTION;

          const eventData = getSparksEventData({
            action: eventAction,
            attempt,
            editable: editableFields[name],
            eventVersion,
            name,
            required: requiredFields[name],
            success,
            value,
          });
          return eventData && put(trackEvent(ONBOARDING_EVENT, eventData));
        })
        .filter(Boolean)
    );
  },
  {
    name: 'onboardingSparksSaga',
  }
);

export const uploadQueuedPhotosSaga = withErrorHandling(function* _uploadQueuedPhotosSaga() {
  const photos = yield* select(getQueuedPhotos);
  const mediaInfos = photos
    .slice(1)
    .map((photo) => ({ blob: photo.url })) as unknown as MediaInfoType[];

  if (!photos.length) {
    return;
  }

  yield* call(uploadMediaSaga, mediaInfos, {
    onUploadFailure(error) {
      executeEvent(I13N_TYPE_FEATURE_COUNT, {
        category: I13N_CATEGORY_ONBOARDING,
        action: I13N_ACTION_PROGRESS,
        label: 'Onboarding',
        message: `Post-login upload failure - ${error}`,
      });
    },
  });

  yield* put(clearQueuedPhotos());
});

/**
 * creates action for notifications in the onboarding form.
 * @param title
 * @param body
 * @returns
 */
const createShowNotificationAction = (title: string, body: string) =>
  showNotification({
    body: [{ id: body }],
    theme: NOTIFICATION_THEME_DARK,
    title: [{ id: title }],
  });

/**
 * After ADD_QUEUED_PHOTO is called, we notify user that media
 * has been uploaded properly.
 */
export function* notifyMediaUploaded() {
  yield* put(createShowNotificationAction('success', 'UploadPhoto.successDescription'));
}

/**
 * After ADD_QUEUED_PHOTO is called, we notify user that media
 * has been uploaded properly.
 */
export function* notifyMediaRemoved() {
  yield* put(createShowNotificationAction('success', 'RemovePhoto.successDescription'));
}
